import { useRef, useState } from 'react';
import type { CSSProperties, PointerEvent, ReactNode } from 'react';

interface FlipCardProps {
  flipped: boolean;
  onClick: () => void;
  front: ReactNode;
  back: ReactNode;
  /** Nút nổi trên thẻ (nghe phát âm) — bấm không làm lật thẻ. */
  overlay?: ReactNode;
  /**
   * Có = thẻ kéo ngang được (chuột hoặc chạm): thả đủ xa thì thẻ bay ra và gọi `onSwipe`.
   * Phải = 'right' (Đã thuộc), trái = 'left' (Chưa thuộc). Trả Promise thì thẻ nằm ngoài tới
   * khi xong; nếu sau đó vẫn là thẻ này (lưu lỗi) nó tự quay về giữa.
   */
  onSwipe?: (direction: SwipeDirection) => void | Promise<void>;
  /** Tạm khoá kéo (đang lưu lần chấm trước). */
  swipeDisabled?: boolean;
}

export type SwipeDirection = 'left' | 'right';

const SWIPE_DISTANCE = 90;
const DRAG_START = 6;
const SWIPE_OUT_X = 640;
const SWIPE_OUT_MS = 180;

const badge = (side: 'left' | 'right', background: string, opacity: number): CSSProperties => ({
  position: 'absolute',
  top: 20,
  // Bên phải chừa chỗ cho nút loa ở góc trên phải của thẻ.
  [side]: side === 'right' ? 72 : 16,
  padding: '6px 12px',
  borderRadius: 999,
  background,
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  opacity,
  pointerEvents: 'none',
});

/** Thẻ lật — màu nền, bóng đổ và vị trí nút loa khớp FlipCard của mobile. */
export function FlipCard({ flipped, onClick, front, back, overlay, onSwipe, swipeDisabled = false }: FlipCardProps) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  // `dx` hiện tại nằm trong ref: pointermove dồn dập có thể chưa kịp render lại trước khi
  // pointerup tới, nên handler đọc state `dx` sẽ thấy giá trị cũ và bỏ lỡ cú vuốt nhanh.
  const drag = useRef<{ startX: number; dx: number; moved: boolean } | null>(null);
  // Thả tay sau khi kéo vẫn sinh ra 1 sự kiện click — cờ này để lần click đó không lật thẻ.
  const suppressClick = useRef(false);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // Bấm vào nút nổi trên thẻ (loa) thì không phải kéo thẻ.
    if (!onSwipe || swipeDisabled || (e.target as HTMLElement).closest('button')) return;
    drag.current = { startX: e.clientX, dx: 0, moved: false };
    // Capture NGAY: nếu không, hất chuột nhanh ra khỏi thẻ thì pointerup rơi ra ngoài, thẻ
    // tưởng vẫn đang bị giữ và bám theo con trỏ ở lần rê chuột sau (trôi ra ngoài khung).
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;
    // Không còn nút nào được giữ mà chưa nhận pointerup (thả ngoài cửa sổ...) → coi như huỷ.
    if (e.pointerType === 'mouse' && e.buttons === 0) return endDrag(true);
    state.dx = e.clientX - state.startX;
    if (!state.moved) {
      if (Math.abs(state.dx) < DRAG_START) return;
      state.moved = true;
      setDragging(true);
    }
    setDx(state.dx);
  };

  const endDrag = (cancelled: boolean) => {
    const state = drag.current;
    drag.current = null;
    if (!state?.moved) return;
    suppressClick.current = true;
    // Click (nếu có) tới ngay sau pointerup; hạ cờ ở tick kế để không nuốt nhầm lần bấm thật sau đó.
    setTimeout(() => (suppressClick.current = false), 0);
    setDragging(false);

    const direction: SwipeDirection | null =
      cancelled ? null : state.dx > SWIPE_DISTANCE ? 'right' : state.dx < -SWIPE_DISTANCE ? 'left' : null;
    if (!direction || !onSwipe) return setDx(0);

    setDx(direction === 'right' ? SWIPE_OUT_X : -SWIPE_OUT_X);
    // Thẻ kế là instance mới (cha đổi `key`); reset chỉ có tác dụng khi thẻ này ở lại.
    setTimeout(() => void Promise.resolve(onSwipe(direction)).finally(() => setDx(0)), SWIPE_OUT_MS);
  };

  const handleClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onClick();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => endDrag(false)}
      onPointerCancel={() => endDrag(true)}
      onLostPointerCapture={() => endDrag(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: 240,
        borderRadius: 'var(--radius-card)',
        background: '#ebf5fc',
        boxShadow: '0 5px 16px rgba(1, 31, 75, 0.26)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        transform: dx === 0 ? undefined : `translateX(${dx}px) rotate(${dx / 30}deg)`,
        transition: dragging ? 'none' : `transform ${SWIPE_OUT_MS}ms ease-out`,
        touchAction: onSwipe ? 'pan-y' : undefined,
        userSelect: onSwipe ? 'none' : undefined,
      }}
    >
      {onSwipe && dx > 0 && <span style={badge('left', 'var(--success)', Math.min(1, dx / SWIPE_DISTANCE))}>Đã thuộc</span>}
      {onSwipe && dx < 0 && <span style={badge('right', 'var(--error)', Math.min(1, -dx / SWIPE_DISTANCE))}>Chưa thuộc</span>}
      {flipped ? back : front}
      {overlay && <div style={{ position: 'absolute', right: 14, top: 14 }}>{overlay}</div>}
      {!flipped && <span style={{ position: 'absolute', bottom: 14, fontSize: 12.5, color: 'var(--text-disabled)' }}>Chạm để lật</span>}
    </div>
  );
}
